import { flushSync } from "react-dom";

import { motion, timing } from "./theme";

/**
 * Cambia algo que repinta la pantalla entera —el tema— con un barrido
 * circular que sale del propio control que se ha pulsado.
 *
 * El truco es la API de transiciones de vista: el navegador saca una foto de
 * cómo está la pantalla, deja que React aplique el cambio, saca otra, y las
 * superpone. A partir de ahí basta con recortar la de después en un círculo
 * que crece desde el botón para que el tema nuevo parezca derramarse desde
 * ahí. Sin esto solo cabía atenuar la pantalla entera, que es el efecto de
 * «se ha recargado algo» en lugar de «esto lo he hecho yo, aquí».
 *
 * Tres cosas que no son obvias:
 *
 * - **`flushSync`.** El navegador saca la segunda foto en cuanto vuelve la
 *   función que se le pasa, y React normalmente agrupa los cambios de estado
 *   para más tarde. Sin forzar el repintado aquí dentro, la foto de después
 *   sale idéntica a la de antes y el barrido descubre lo mismo que tapaba.
 * - **El radio.** Tiene que llegar a la esquina más lejana, no al borde más
 *   cercano: si se queda corto, la última esquina cambia de golpe justo al
 *   final, que es lo único que se recuerda de toda la animación.
 * - **Quién se mueve.** Se anima solo la foto nueva y se deja la vieja quieta
 *   debajo. Animar las dos las cruza en un fundido y el círculo se ve
 *   translúcido en lugar de nítido.
 *
 * Donde no haya transiciones de vista —Firefox, Safari antiguo— o donde se
 * haya pedido menos movimiento, el cambio se aplica igual, sin adorno. Es un
 * remate, no el mecanismo.
 */
export function cambiarConBarrido(origen, aplicar) {
  const documento = typeof document === "undefined" ? null : document;
  const quietud =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  if (!documento?.startViewTransition || quietud || !origen) {
    aplicar();
    return;
  }

  const caja = origen.getBoundingClientRect();
  const cx = caja.left + caja.width / 2;
  const cy = caja.top + caja.height / 2;
  const ancho = window.innerWidth;
  const alto = window.innerHeight;
  const radio = Math.hypot(Math.max(cx, ancho - cx), Math.max(cy, alto - cy));

  const transicion = documento.startViewTransition(() => {
    flushSync(aplicar);
  });

  transicion.ready
    .then(() => {
      documento.documentElement.animate(
        {
          clipPath: [
            `circle(0px at ${cx}px ${cy}px)`,
            `circle(${radio}px at ${cx}px ${cy}px)`,
          ],
        },
        {
          duration: timing.intercambio,
          easing: motion.recorrido,
          pseudoElement: "::view-transition-new(root)",
        },
      );
    })
    // Si el navegador descarta la transición —otra encima, la pestaña oculta—
    // el tema ya ha cambiado; lo único que se pierde es el barrido.
    .catch(() => {});
}
